import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowLeft, KeyRound, Mail } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from '../components/LanguageSelector';
import './LoginPage.css';
import './RegisterPage.css';

function ForgotPasswordPage() {
  const { t } = useTranslation('login');
  const location = useLocation();
  const navigate = useNavigate();
  const { signInWithToken } = useAuth();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);

  const errorText = (err) => {
    const detail = err.response?.data?.detail;
    if (detail === 'invalid_code') return t('forgot.errors.invalidCode');
    if (err.response?.status === 429) return t('forgot.errors.tooManyAttempts');
    if (err.response?.status === 422 && Array.isArray(detail)) return detail.map((e) => e.msg).join('. ');
    return t('forgot.errors.generic');
  };

  const sendCode = async () => {
    setError('');
    try {
      await api.forgotPassword(email);
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (await sendCode()) setStep('code');
    setLoading(false);
  };

  const handleResend = async () => {
    if (await sendCode()) {
      setResent(true);
      setTimeout(() => setResent(false), 30000);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.resetPassword(email, code, newPassword);
      await signInWithToken(data.access_token);
      navigate('/');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-language-selector">
        <LanguageSelector />
      </div>
      <div className="login-container">
        <div className="login-header">
          {step === 'email' ? (
            <>
              <KeyRound size={40} className="login-icon" />
              <h1>{t('forgot.title')}</h1>
              <p>{t('forgot.subtitle')}</p>
            </>
          ) : (
            <>
              <Mail size={40} className="login-icon" />
              <h1>{t('forgot.title')}</h1>
              <p data-testid="forgot-code-subtitle">{t('forgot.codeSubtitle', { email })}</p>
            </>
          )}
        </div>

        {error && (
          <div className="error-message" data-testid="forgot-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {step === 'email' ? (
          <form onSubmit={handleSend} className="login-form">
            <div className="form-group">
              <label htmlFor="email">{t('form.email')}</label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder={t('form.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary submit-btn" disabled={loading}>
              {loading ? <><div className="spinner" />{t('forgot.sending')}</> : t('forgot.send')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="login-form">
            <div className="form-group">
              <label htmlFor="code">{t('forgot.code')}</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="input verify-code-input"
                placeholder={t('forgot.codePlaceholder')}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                minLength={6}
                maxLength={6}
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-password">{t('forgot.newPassword')}</label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                className="input"
                placeholder={t('forgot.newPasswordPlaceholder')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                maxLength={128}
              />
            </div>
            <button type="submit" className="btn btn-primary submit-btn" disabled={loading || code.length !== 6}>
              {loading ? <><div className="spinner" />{t('forgot.submitting')}</> : t('forgot.submit')}
            </button>
          </form>
        )}

        <div className="verify-actions">
          {step === 'code' && (
            <button className="link-button" onClick={handleResend} disabled={resent}>
              {resent ? t('forgot.resent') : t('forgot.resend')}
            </button>
          )}
          <Link to="/login" className="link-button">
            <ArrowLeft size={14} />
            {t('forgot.back')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
